import {
    FormPageLayout,
    SimplePageContentWrapper
} from "@flexinet/ui-components";
import {
    Badge,
    Box,
    Card,
    Group,
    Stack,
    Text
} from "@mantine/core";
import {
    useNavigate,
    useSearchParams
} from "react-router-dom";
import {useUserAccount} from "../../../../hooks/useUserAccount";
import {
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {fetchProduct} from "../../products/services/ProductsService";
import {fetchPricesByProduct} from "../../prices/services/PricesService";
import {useState} from 'react';
import {showNotification} from '@mantine/notifications';
import {Cache} from 'aws-amplify/utils';
import {
    CancelButton,
    CreateSubscriptionButton
} from '../buttons';
import { createSubscription } from '../../subscriptions/services/SubscriptionsService';

export const CreateSubscriptionPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const productId = searchParams.get('productId');
    const priceId = searchParams.get('priceId');

    const {data: product} = useQuery({
                                         queryKey: ['product', productId],
                                         queryFn: () => fetchProduct(productId!),
                                         enabled: !!productId
                                     });

    const {data: prices} = useQuery({
                                        queryKey: ['prices', productId],
                                        queryFn: () => fetchPricesByProduct(productId!),
                                        enabled: !!productId
                                    });

    const {userAccount: account} = useUserAccount();
    const [processing, setProcessing] = useState(false);

    const handleCreateSubscription = async () => {
        setProcessing(true);

        try {
            const price = priceId && prices ? prices.find((p: any) => p.id === priceId) : prices?.[0];
            
            if (!price) {
                throw new Error('No price found for product');
            }
            if (!account?.customerId) {
                throw new Error('No customer ID found in account');
            }

            // Billing API handles provider integration
            await createSubscription({
                customerId: account.customerId,
                items: [{ priceId: price.id, quantity: 1 }],
                status: 'Active'
            });

            showNotification({
                title: 'Subscription Created',
                message: 'Your subscription has been created successfully',
                color: 'teal',
            });

            queryClient.invalidateQueries({queryKey: ['subscriptions']});
            
            const returnTo = searchParams.get('returnTo') || await Cache.getItem('returnTo');
            if (returnTo) {
                navigate(`${decodeURIComponent(returnTo)}?success=true`);
            } else {
                navigate('/products?success=true');
            }
        } catch (error) {
            console.error('Failed to create subscription:', error);
            
            let errorMessage = 'Failed to create subscription';
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            
            showNotification({
                title: 'Subscription Failed',
                message: errorMessage,
                color: 'red',
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleCancel = () => {
        const params = new URLSearchParams(searchParams);
        navigate(`/stripe-subscriptions/select-payment-method?${params.toString()}`);
    };

    const price = prices?.[0];

    return (
        <FormPageLayout
            title='Create Subscription'
            pageNavigation={<Box/>}
        >
            <SimplePageContentWrapper>
                <Stack gap="lg">
                    <Badge color="green" variant="light" size="lg">
                        Create Stripe Subscription
                    </Badge>

                    {product && (
                        <Card withBorder p="md">
                            <Text size="lg" fw={600} mb="xs">{product.name}</Text>
                            {product.shortDescription && (
                                <Text size="sm" c="dimmed" mb="md">{product.shortDescription}</Text>
                            )}
                            {price && (
                                <Text size="md" fw={500}>
                                    ${(price.amount / 100).toFixed(2)} / {price.billingPeriod}
                                </Text>
                            )}
                        </Card>
                    )}

                    <Card withBorder p="md">
                        <Stack gap="md">
                            <Text size="md" fw={600}>Ready to Subscribe</Text>
                            <Text size="sm" c="dimmed">
                                Your payment method is ready. Click below to create your subscription.
                            </Text>
                        </Stack>
                    </Card>

                    <Group justify="center" gap="md">
                        <CancelButton onClick={handleCancel} disabled={processing}>
                            Back
                        </CancelButton>
                        <CreateSubscriptionButton
                            loading={processing}
                            onClick={handleCreateSubscription}
                        />
                    </Group>
                </Stack>
            </SimplePageContentWrapper>
        </FormPageLayout>
    );
};